/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  Card, DetailStack, FontWeight, Message, Text,
} from '@doveit/bricks';
import { CardHeaderProps } from '@doveit/bricks/dist/typings/components/card/composition';
import { pluralizeWord } from '@doveit/hammer';
import { Agent } from '../../../providers/api/dtos';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import { useAgent } from '../../../hooks/use-agent/useAgent';
import { AgentAvatar } from '../../components';
import * as styles from './ViewAgentPreview.style';
import { jobTitleLabels } from '../../../labels';
import { NO_VALUE_SYMBOL } from '../../../property/utils';
import { setVerticalEllipsis } from '../../../utils/styles/verticalEllipsis.style';
import ViewEntityPreviewSkeleton from '../../../components/view-entity-preview-skeleton/ViewEntityPreviewSkeleton';

interface ViewAgentPreviewBaseProps extends React.AriaAttributes, Pick<CardHeaderProps, 'title' | 'primaryActions'> {
  hideHeader?: boolean,
  hiddenFields?: Partial<Record<keyof Agent, boolean>>,
}

export type ViewAgentPreviewProps = ViewAgentPreviewBaseProps & ({
  agentId: NonNullable<Agent['id']>,
  agent?: undefined,
} | {
  agent: Agent,
  agentId?: undefined,
});

export const LOAD_AGENT_DATA_ERROR_MESSAGE = 'Non è stato possibile caricare i dati dell\'agente.';

const ViewAgentPreview: React.FC<ViewAgentPreviewProps> = ({
  agentId,
  title = 'Agente',
  primaryActions,
  agent: externalAgent,
  hideHeader = false,
  hiddenFields,
  ...rest
}) => {
  const isMobile = useIsDevice('mobile');
  const { data: loadedAgent, isLoading: isAgentLoading, error: agentError } = useAgent(agentId);

  const agent = externalAgent || loadedAgent;

  if ((isAgentLoading) && !agentError) {
    return (
      <ViewEntityPreviewSkeleton aria-label="Informazioni agente in caricamento" />
    );
  }

  if (agentError) {
    return (
      <Message
        type="critical"
        message={LOAD_AGENT_DATA_ERROR_MESSAGE}
      />
    );
  }

  return agent && (
    <Card
      aria-label="Anteprima agente"
      {...rest}
    >
      {!hideHeader && (
        <Card.Header
          aria-label="Anteprima agente (header)"
          title={title}
          primaryActions={primaryActions}
        />
      )}
      <Card.Content>
        <div css={styles.basic}>
          <div css={styles.info}>
            <AgentAvatar
              size={isMobile ? 'M' : 'L'}
              inTrial={agent.inTrial}
              needsSupervisor={agent.needsSupervisor}
              photoId={agent.photoId}
            />
            <div css={styles.textInfo}>
              <Text.Body
                lineHeight="1"
                as="div"
                fontWeight={FontWeight.MEDIUM}
              >
                {agent.name.name} {agent.name.surname}
              </Text.Body>
              {!hiddenFields?.email && agent.email.work && (
                <Text.BodySmall
                  fontWeight={FontWeight.REGULAR}
                  as="div"
                >
                  {agent.email.work}
                </Text.BodySmall>
              )}
              {!hiddenFields?.mobile && agent.mobile.work && (
                <Text.BodySmall
                  fontWeight={FontWeight.REGULAR}
                  as="div"
                >
                  {agent.mobile.work}
                </Text.BodySmall>
              )}
            </div>
          </div>
          {isMobile && (<Card.Divider />)}
          <div css={styles.detailsWrapper}>
            <div css={styles.details}>
              {!hiddenFields?.jobTitle && (
                <div>
                  <DetailStack
                    label="Ruolo"
                    size="M"
                    width={isMobile ? undefined : '7.5rem'}
                  >
                    {jobTitleLabels[agent.jobTitle]}
                  </DetailStack>
                </div>
              )}
              {!hiddenFields?.coveredGeo && (
                <div>
                  <DetailStack
                    label={`${agent.coveredGeo?.length || 0} ${pluralizeWord('provinc', 'ia', 'e', agent.coveredGeo?.length || 0)}`}
                    aria-label="Province coperte dall'agente"
                    size="M"
                    width={isMobile ? undefined : '7.5rem'}
                  >
                    <div css={setVerticalEllipsis(1)}>
                      {(agent.coveredGeo && agent.coveredGeo?.length > 0)
                        ? agent.coveredGeo?.map(({ province }) => province).join(', ')
                        : NO_VALUE_SYMBOL}
                    </div>
                  </DetailStack>
                </div>
              )}
              {!hiddenFields?.fiscalCode && (
                <div>
                  <DetailStack
                    label="Codice fiscale"
                    size="M"
                  >
                    {agent.fiscalCode ?? NO_VALUE_SYMBOL}
                  </DetailStack>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card.Content>
    </Card>
  );
};

export default ViewAgentPreview;
